import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type RefObject,
} from 'react';

/** The ⓘ control's callbacks, bound by the row that owns one dataset. Stable across renders. */
export type DatasetInfoControls = {
    /** Pointer or focus arrived on (key) or left (undefined) a ⓘ control. */
    onHover: (key: string | undefined) => void;
    /** Activating a ⓘ control pins its details, or releases them when already pinned. */
    onToggle: (key: string) => void;
};

export type DatasetInfoState = {
    /** The dataset whose details are showing, if any. */
    openKey: string | undefined;
    pinnedKey: string | undefined;
    controls: DatasetInfoControls;
};

/**
 * Which dataset's details are showing: the one under the pointer or focus,
 * else the one pinned by a click. One at a time, so hovering another ⓘ
 * temporarily replaces a pinned card rather than stacking on it. Releasing a
 * pin (a second activation, or Escape) closes the card outright, even though
 * the control is still hovered or focused; it shows again once the pointer
 * or focus leaves and returns. Escape is taken from `panel`, the element
 * holding the ⓘ controls, and consumed there: the panel's window-level close
 * only fires once nothing is pinned, and a dialog above the panel, which the
 * key never passes through, keeps its own Escape.
 */
export function useDatasetInfo(
    panel: RefObject<HTMLElement>
): DatasetInfoState {
    const [hoverKey, setHoverKey] = useState<string>();
    const [pinnedKey, setPinnedKey] = useState<string>();

    useEffect(() => {
        const element = panel.current;
        if (pinnedKey === undefined || !element) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                setPinnedKey(undefined);
                setHoverKey(undefined);
            }
        };
        element.addEventListener('keydown', onKeyDown);
        return () => element.removeEventListener('keydown', onKeyDown);
    }, [panel, pinnedKey]);

    const onToggle = useCallback((key: string) => {
        setPinnedKey((current) => {
            if (current === key) {
                setHoverKey(undefined);
                return undefined;
            }
            return key;
        });
    }, []);

    const controls = useMemo(
        () => ({ onHover: setHoverKey, onToggle }),
        [onToggle]
    );

    return { openKey: hoverKey ?? pinnedKey, pinnedKey, controls };
}
